import React, { useEffect, useState } from "react";
import { Box, Stack, Typography } from "@mui/material";
import moment from "moment";
import {
  describeCheckInCadence,
  describeCoachReviewsSummary,
  describeDurationWeeks,
  describeJourneyWindowFromPublish,
  formatDisplayDate,
  getJourneyStartLabel,
  getTrackerAccessLabel,
  normalizeJourneyStartsFrom,
  normalizeTrackerAccess,
  isLeadCaptureTracker,
  REPLY_WITHIN_DAYS_OPTIONS,
  SCHEDULED_REVIEWS_OPTIONS,
  TRACKER_ACCESS,
} from "./trackerStorage";
import {
  getFieldTypeLabel,
  getMeasureTypeLabel,
} from "./questions/questionStorage";
import {
  countUniquePeople,
  loadSubmissionsFromApi,
} from "./submissionStorage";

const PRIMARY = "var(--portal-theme-primary)";
const PRIMARY_SOFT =
  "color-mix(in srgb, var(--portal-theme-primary) 12%, white)";
const BORDER = "#e2e8f0";
const MUTED = "#64748b";
const INK = "#0f172a";

const Section = ({ title, subtitle, action, children }) => (
  <Box
    sx={{
      height: "100%",
      p: { xs: 2, sm: 2.25 },
      borderRadius: "12px",
      border: `1px solid ${BORDER}`,
      bgcolor: "#fff",
      boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
    }}
  >
    <Stack
      direction="row"
      alignItems="flex-start"
      justifyContent="space-between"
      spacing={1.5}
      sx={{ mb: 1.5 }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 15, fontWeight: 700, color: INK }}>
          {title}
        </Typography>
        {subtitle ? (
          <Typography sx={{ mt: 0.25, fontSize: 12.5, color: MUTED }}>
            {subtitle}
          </Typography>
        ) : null}
      </Box>
      {action}
    </Stack>
    {children}
  </Box>
);

/** Simple label → value rows — no dense grids. */
const InfoRows = ({ rows }) => (
  <Box>
    {rows
      .filter((row) => row && row.value != null && row.value !== "")
      .map((row) => (
        <Box
          key={row.label}
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: { xs: 0.25, sm: 1.5 },
            py: 1,
            borderBottom: `1px solid #f1f5f9`,
            "&:last-child": { borderBottom: 0 },
          }}
        >
          <Typography
            sx={{
              flex: { sm: "0 0 140px" },
              fontSize: 12.5,
              fontWeight: 600,
              color: MUTED,
            }}
          >
            {row.label}
          </Typography>
          <Typography
            sx={{
              flex: 1,
              fontSize: 13.5,
              fontWeight: 600,
              color: INK,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {row.value}
          </Typography>
        </Box>
      ))}
  </Box>
);

const Empty = ({ children }) => (
  <Typography sx={{ fontSize: 13.5, color: MUTED }}>{children}</Typography>
);

const isMeasurableQuestion = (q) =>
  q?.measurable === true ||
  String(q?.mode || "").toLowerCase() === "measurable" ||
  Boolean(String(q?.measureType || "").trim());

/** General / Measurable questions — one small tab each. */
const QuestionsByMode = ({ questions = [] }) => {
  const general = questions.filter((q) => !isMeasurableQuestion(q));
  const measurable = questions.filter(isMeasurableQuestion);
  const [mode, setMode] = useState(
    general.length || !measurable.length ? "general" : "measurable"
  );
  const list = mode === "general" ? general : measurable;
  const tabs = [
    { id: "general", label: "General", count: general.length },
    { id: "measurable", label: "Measurable", count: measurable.length },
  ];

  return (
    <Box>
      <Box sx={{ display: "flex", gap: 0.75, mb: 0.75 }}>
        {tabs.map((tab) => {
          const selected = mode === tab.id;
          return (
            <Box
              key={tab.id}
              component="button"
              type="button"
              onClick={() => setMode(tab.id)}
              sx={{
                px: 1.25,
                py: 0.35,
                borderRadius: "999px",
                border: `1px solid ${selected ? PRIMARY : BORDER}`,
                bgcolor: selected ? PRIMARY_SOFT : "#fff",
                color: selected ? PRIMARY : MUTED,
                fontSize: 12.5,
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              {tab.label} {tab.count}
            </Box>
          );
        })}
      </Box>
      {list.length === 0 ? (
        <Empty>No {mode} questions yet.</Empty>
      ) : (
        <Stack spacing={0}>
          {list.map((question, index) => {
            const statement =
              question.statement ||
              question.label ||
              question.title ||
              "Untitled question";
            // Measurable: the measure (e.g. Weight), not just "Number".
            const typeLabel =
              mode === "measurable" && question.measureType
                ? getMeasureTypeLabel(question.measureType)
                : getFieldTypeLabel(question.fieldType || question.type);
            return (
              <Box
                key={question.id || index}
                sx={{
                  py: 1.15,
                  borderBottom: `1px solid #f1f5f9`,
                  "&:last-child": { borderBottom: 0 },
                }}
              >
                <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: INK }}>
                  {index + 1}. {statement}
                </Typography>
                <Typography sx={{ mt: 0.5, fontSize: 12, color: MUTED }}>
                  {[typeLabel, question.required ? "Required" : ""]
                    .filter(Boolean)
                    .join(" · ") || "—"}
                </Typography>
              </Box>
            );
          })}
        </Stack>
      )}
    </Box>
  );
};

/**
 * Coach view of the tracker setup: only what a coach needs to work with
 * their members (who joins, timing, check-ins, coach review, questions).
 * Admin-only setup (publish & intake, schedule points, counts) is left out.
 */
export default function TrackerDetailInfoTab({ tracker }) {
  const [submissionStats, setSubmissionStats] = useState(null);
  const statsTrackerId = tracker?.id || tracker?._id || "";

  useEffect(() => {
    if (!statsTrackerId) return undefined;
    let cancelled = false;
    setSubmissionStats(null);
    loadSubmissionsFromApi(statsTrackerId, { limit: 500 }).then((result) => {
      if (cancelled) return;
      setSubmissionStats(
        result.ok
          ? {
              people: countUniquePeople(result.rows),
              submissions: result.total ?? result.rows.length,
            }
          : null
      );
    });
    return () => {
      cancelled = true;
    };
  }, [statsTrackerId]);

  if (!tracker) return null;

  const isLeadCapture = isLeadCaptureTracker(tracker);
  const setup = tracker.createSetup;
  const programmes = tracker.programmes || [];
  const sharedQuestions = tracker.sharedQuestions || [];
  const coachReview = tracker.coachReview || {};
  const access = normalizeTrackerAccess(tracker.trackerAccess, tracker);
  const publishDate = setup?.publishDate || tracker.publishDate || null;
  const journeyStartsFrom = normalizeJourneyStartsFrom(
    setup?.journeyStartsFrom || tracker.journeyStartsFrom
  );

  // "—" while loading or if the submissions API fails (no local fallback).
  const peopleCount = submissionStats ? submissionStats.people : "—";

  const scheduledReviewsLabel =
    SCHEDULED_REVIEWS_OPTIONS.find(
      (item) => item.value === (setup?.scheduledReviews || "none")
    )?.label || "—";
  const replyWithinLabel =
    REPLY_WITHIN_DAYS_OPTIONS.find(
      (item) => item.value === Number(setup?.replyWithinDays)
    )?.label || null;

  const summaryBits = isLeadCapture
    ? [
        "Lead Capture",
        `${sharedQuestions.length} extra question${
          sharedQuestions.length === 1 ? "" : "s"
        }`,
        `${peopleCount} ${peopleCount === 1 ? "person" : "people"}`,
      ].filter(Boolean)
    : [
        setup
          ? describeDurationWeeks(setup.durationWeeks, setup.customDurationDays)
          : null,
        `${sharedQuestions.length} question${
          sharedQuestions.length === 1 ? "" : "s"
        }`,
        `${peopleCount} ${peopleCount === 1 ? "member" : "members"}`,
      ].filter(Boolean);

  return (
    <Box>
      <Box
        sx={{
          width: "100%",
          mb: 2,
          p: { xs: 2, sm: 2.25 },
          borderRadius: "12px",
          border: `1px solid ${BORDER}`,
          bgcolor: "#fff",
          boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ sm: "flex-start" }}
          justifyContent="space-between"
          spacing={1.5}
        >
          <Box>
            <Typography sx={{ fontSize: 18, fontWeight: 700, color: INK }}>
              {tracker.name ||
                (isLeadCapture ? "Untitled lead capture" : "Untitled journey")}
            </Typography>
            <Typography
              sx={{ mt: 0.5, fontSize: 13, color: MUTED, lineHeight: 1.5 }}
            >
              {summaryBits.join(" · ")}
            </Typography>
          </Box>
        </Stack>
      </Box>

      <div className="row">
        <div className="col-12 col-lg-6 mt-3">
          <Section title="Basics" subtitle="Who joins and when it starts">
            <InfoRows
              rows={[
                {
                  label: "Access",
                  value: getTrackerAccessLabel(tracker.trackerAccess, tracker),
                },
                access === TRACKER_ACCESS.GYMRITE_MEMBERS
                  ? {
                      label: "Programme",
                      value: programmes.length
                        ? programmes
                            .map((item) => item.title || item)
                            .join(", ")
                        : "No programme — only members with an active programme can see it",
                    }
                  : null,
                !isLeadCapture
                  ? {
                      label: "Journey starts",
                      value: getJourneyStartLabel(journeyStartsFrom),
                    }
                  : {
                      label: "Type",
                      value: "Lead Capture · Standard",
                    },
                // The publish date only matters when the journey starts on it.
                !isLeadCapture &&
                journeyStartsFrom === "from_publish_date" &&
                publishDate
                  ? {
                      label: "Start date",
                      value:
                        formatDisplayDate(publishDate) ||
                        moment(publishDate).format("D MMM YYYY"),
                    }
                  : null,
                tracker.description
                  ? { label: "Internal note", value: tracker.description }
                  : null,
              ]}
            />
          </Section>
        </div>

        <div className="col-12 col-lg-6 mt-3">
          {isLeadCapture ? (
            <Section
              title="Questions"
              subtitle="Extra questions on this lead form"
            >
              {sharedQuestions.length === 0 ? (
                <Empty>No extra questions added.</Empty>
              ) : (
                <Stack spacing={0}>
                  {sharedQuestions.map((question, index) => {
                    const typeLabel = getFieldTypeLabel(
                      question.fieldType || question.type
                    );
                    return (
                      <Box
                        key={question.id || index}
                        sx={{
                          py: 1.15,
                          borderBottom: `1px solid #f1f5f9`,
                          "&:last-child": { borderBottom: 0 },
                        }}
                      >
                        <Typography
                          sx={{ fontSize: 13.5, fontWeight: 600, color: INK }}
                        >
                          {index + 1}.{" "}
                          {question.label ||
                            question.statement ||
                            "Untitled question"}
                        </Typography>
                        <Typography sx={{ mt: 0.5, fontSize: 12, color: MUTED }}>
                          {question.required ? "Required" : "Optional"}
                          {typeLabel ? ` · ${typeLabel}` : ""}
                        </Typography>
                      </Box>
                    );
                  })}
                </Stack>
              )}
            </Section>
          ) : setup ? (
            <Section
              title="Journey length & check-ins"
              subtitle="How long it runs and how often members submit"
            >
              <InfoRows
                rows={[
                  {
                    label: "Duration",
                    value: describeDurationWeeks(
                      setup.durationWeeks,
                      setup.customDurationDays
                    ),
                  },
                  {
                    label: "Check-in cadence",
                    value: describeCheckInCadence(setup),
                  },
                  {
                    label: "Journey window",
                    value: describeJourneyWindowFromPublish(setup),
                  },
                  {
                    label: "Coach on every check-in",
                    value: setup.alsoReviewEveryCheckin
                      ? replyWithinLabel
                        ? `Yes · within ${replyWithinLabel}`
                        : "Yes"
                      : "No",
                  },
                ]}
              />
            </Section>
          ) : (
            <Section
              title="Journey length & check-ins"
              subtitle="How long it runs and how often members submit"
            >
              <Empty>Set duration and check-ins in the builder.</Empty>
            </Section>
          )}
        </div>

        {!isLeadCapture && (
        <div className="col-12 col-lg-6 mt-3">
          <Section
            title="Coach review"
            subtitle="When coaches are asked to reply"
          >
            {coachReview.enabled === false ? (
              <Empty>Coach review is off for this tracker.</Empty>
            ) : (
              <InfoRows
                rows={[
                  { label: "Status", value: "Enabled" },
                  setup
                    ? { label: "Schedule", value: scheduledReviewsLabel }
                    : null,
                  {
                    label: "How it works",
                    value: setup
                      ? describeCoachReviewsSummary(setup).replace(
                          /\. The coach is reminded by the Review reminder\.$/,
                          ""
                        )
                      : "—",
                  },
                ]}
              />
            )}
          </Section>
        </div>
        )}

        {!isLeadCapture && (
        <div className="col-12 col-lg-6 mt-3">
          <Section
            title="Questions"
            subtitle="What members answer on check-ins"
          >
            <QuestionsByMode questions={sharedQuestions} />
          </Section>
        </div>
        )}

      </div>
    </Box>
  );
}
